import { Component } from 'react';

import { connect } from 'react-redux';
import { Redirect } from 'react-resource-router';
import { compose } from 'redux';

import Spinner from '@atlaskit/spinner';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/analytics-bridge';

import { Repository } from 'src/components/types';
import withLocation from 'src/router/utils/with-location';
import { Loading } from 'src/sections/global/components/loading.style';
import {
  LegacyRouteProps,
  Ref,
  RepositoryPrivilege,
} from 'src/sections/repository/types';
import { getUpToSmallBreakpointState } from 'src/selectors/global-selectors';
import { getCurrentRepository } from 'src/selectors/repository-selectors';
import { getCurrentUser } from 'src/selectors/user-selectors';
import { BucketState } from 'src/types/state';
import urls from 'src/urls/source';
import { safeDecodePath } from 'src/utils/path';

import EmptyState from '../../../containers/empty-state';
import loadFileTree from '../actions/load-file-tree';
import loadSourceObject from '../actions/load-source-object';
import SourcePage from '../components';
import DirectoryHeader from '../components/directory-header';
import FileHeader from '../components/file-header';
import SourceBrowser from '../components/source-browser';
import { FilterMode } from '../constants';
import { getFilterMode, getFilterQuery } from '../reducers/section';
import { FilteredFiles, SourceObject } from '../types';

const mapDispatchToProps = {
  loadFileTree,
  loadSourceObject,
};

const mapStateToProps = (state: BucketState) => {
  const repository = getCurrentRepository(state);
  const currentUser = getCurrentUser(state);
  const { ref, hash, isLoading, filteredFiles, sourceObject } =
    state.repository.source.section;

  const filterQuery = getFilterQuery(state);
  const filterMode = getFilterMode(state);
  const isUpToSmallBreakpointActive = getUpToSmallBreakpointState(state);

  return {
    currentRef: ref,
    filteredFiles,
    hash,
    isLoading,
    repository,
    repositoryAccessLevel: state.repository.details.accessLevel,
    sourceObject,
    userUuid: currentUser ? currentUser.uuid : undefined,
    filterMode,
    filterQuery,
    isUpToSmallBreakpointActive,
  };
};

export type Params = LegacyRouteProps<{
  path?: string;
  refOrHash?: string;
}>;

type Props = Params & {
  currentRef: Ref | null | undefined;
  filteredFiles: FilteredFiles;
  hash: string | null | undefined;
  isLoading: boolean;
  loadFileTree: typeof loadFileTree;
  loadSourceObject: typeof loadSourceObject;
  repository: Repository | null | undefined;
  repositoryAccessLevel: RepositoryPrivilege;
  sourceObject: SourceObject | null | undefined;
  userUuid: string | undefined;
  filterMode: FilterMode;
  filterQuery?: string;
  isUpToSmallBreakpointActive: boolean;
};

class SourceBrowserWithLifecycle extends Component<Props> {
  componentDidMount() {
    this.loadSourceObject();
    this.loadFileTree();
  }

  componentDidUpdate(prevProps: Props) {
    this.loadSourceObject(prevProps);
    this.loadFileTree(prevProps);
  }

  getPath = (props: Props) => {
    const { path } = props.match.params;
    return safeDecodePath(path || '');
  };

  didRepoOrHashChange = (prevProps: Props) => {
    const { hash: prevHash, repository: prevRepository } = prevProps;
    const { hash, repository } = this.props;
    const repoChanged =
      (repository && repository.full_name) !==
      (prevRepository && prevRepository.full_name);
    const hashChanged = hash !== prevHash;
    return repoChanged || hashChanged;
  };

  loadFileTree = (prevProps?: Props) => {
    const { hash, repository } = this.props;
    if (!hash || !repository) {
      return;
    }

    const shouldLoad = !prevProps || this.didRepoOrHashChange(prevProps);
    if (shouldLoad) {
      this.props.loadFileTree(repository, hash);
    }
  };

  loadSourceObject = (prevProps?: Props) => {
    const { hash, repository } = this.props;
    if (!repository || !hash) {
      return;
    }

    const path = this.getPath(this.props);
    const shouldLoad =
      !prevProps ||
      this.getPath(prevProps) !== path ||
      this.didRepoOrHashChange(prevProps);

    if (shouldLoad) {
      this.props.loadSourceObject(repository, hash, path);
    }
  };

  // explicitly neither accept nor pass input parameters (like event objects) for use as a callback prop
  reloadSourceObject = () => {
    this.loadSourceObject();
  };

  render() {
    const {
      currentRef,
      filteredFiles,
      hash,
      isLoading,
      location,
      repository,
      repositoryAccessLevel,
      sourceObject,
      userUuid,
      filterMode,
      filterQuery,
      isUpToSmallBreakpointActive,
    } = this.props;

    if (isLoading || !repository) {
      return (
        <Loading>
          <Spinner size="large" />
        </Loading>
      );
    }

    if (!hash) {
      return <EmptyState history={this.props.history} />;
    }

    const path = this.getPath(this.props);
    const { url, params } = this.props.match;

    // The URL must include a ref or hash to allow for relative links when
    // browsing files. Redirect if needed.
    if (!params.refOrHash) {
      if (!currentRef) {
        return <EmptyState history={this.props.history} />;
      }
      const to = urls.ui.sourceWithRef(repository.full_name, currentRef);
      return <Redirect to={`${to}${location.search}`} />;
    }

    const treeEntry = sourceObject && sourceObject.treeEntry;
    const isDirectory =
      (treeEntry && treeEntry.type === 'directory') || url.endsWith('/');
    const sourcePath = sourceObject && sourceObject.path;

    if (isDirectory) {
      // Directory URLs should end with a trailing slash to allow for relative
      // links when browsing files, only do this if the treeEntry is current.
      if (url.slice(-1) !== '/' && path === sourcePath) {
        return <Redirect to={`${url}/${location.search}`} />;
      }
    }

    const header = isDirectory ? (
      <DirectoryHeader path={sourcePath || path} filterMode={filterMode} />
    ) : (
      <FileHeader path={sourcePath || path} />
    );

    return (
      <ContextualAnalyticsData
        sourceName="repositorySource"
        sourceType={SCREEN}
      >
        <SourcePage
          header={header}
          path={sourcePath || path}
          pathType={isDirectory ? 'directory' : 'file'}
          repository={repository}
          repositoryAccessLevel={repositoryAccessLevel}
          userUuid={userUuid}
          isUpToSmallBreakpointActive={isUpToSmallBreakpointActive}
        >
          <SourceBrowser
            filteredFiles={filteredFiles}
            isDirectory={isDirectory}
            loadSourceObject={this.reloadSourceObject}
            repository={repository}
            sourceObject={sourceObject}
            filterMode={filterMode}
            filterQuery={filterQuery}
          />
        </SourcePage>
      </ContextualAnalyticsData>
    );
  }
}

export default compose<React.ComponentType<Params>>(
  withLocation,
  connect(mapStateToProps, mapDispatchToProps)
)(SourceBrowserWithLifecycle);
